import { sumBy, toArray } from 'lodash';

import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { CashUnits } from '@pos/app/scripts/services/cash-units';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { EndOfShift } from '@pos/app/scripts/services/end-of-shift';
import { Persist } from '@core/app/scripts/services/persist';
import { SessionJsonStorage } from '@shared/app/scripts/services/session-json-storage';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { Withdrawal } from '@shared/app/scripts/services/withdrawal';

const { of } = require('rxjs');

/*
 * Component responsible for /eos-edit route.
 */
angular
  .module('posApp')
  .directive(
    'eosEdit',
    (
      cashUnits: CashUnits,
      displayMessage: DisplayMessage,
      endOfShift: EndOfShift,
      navigateTo: NavigateTo,
      paymentTypes,
      persist: Persist,
      pos,
      legacyPrint,
      sessionJsonStorage: SessionJsonStorage,
      settingsManager: SettingsManager,
      translate: ng.translate.ITranslateService,
      withdrawal: Withdrawal,
    ) => ({
      restrict: 'E',

      scope: true,

      template: require('./eos-edit.html').default,

      link: function ($scope, element, attrs) {
        const $translate = translate;

        $scope.editFocus$ = of(undefined);

        $scope.paymentMethods = settingsManager.settings.paymentMethods;

        // TODO unit tests
        $scope.updateBalance = () => {
          endOfShift.eos.difference =
            $scope.balance -
            endOfShift.eos.expectedCashInDrawer +
            $scope.getElectronicDifference() +
            $scope.getOthersDifference();

          $scope.updateDeposit();
        };

        $scope.updateDeposit = () => {
          let deposit = Number(endOfShift.eos.deposit) || 0;
          let balance = Number($scope.balance) || 0;

          endOfShift.eos.endBalance = balance - deposit;
        };

        $scope.calculateCoinsTotal = () => {
          const coinTotal = _.reduce(
            $scope.coinTypes,
            (memo, entity) => memo + entity.total,
            0,
          );

          // note! plus is casting it back to number
          $scope.coinTotal = $scope.balance = +coinTotal.toFixed(2);

          $scope.updateBalance();
        };

        $scope.getElectronicTotal = () => {
          if (!$scope.eos || !$scope.eos.final) {
            return 0;
          }

          return $scope.eos.final.creditcard + $scope.eos.final.pin;
        };

        $scope.getElectronicDifference = () => {
          if (!$scope.eos || !$scope.eos.final || !$scope.eos.original) {
            return 0;
          }

          return (
            $scope.eos.final.creditcard -
            $scope.eos.original.creditcard +
            ($scope.eos.final.pin - $scope.eos.original.pin)
          );
        };

        $scope.getOthersTotal = () => {
          if (!$scope.eos || !$scope.eos.final) {
            return 0;
          }

          return (
            $scope.eos.final.couponin +
            $scope.eos.final.couponout +
            $scope.eos.final.creditreceipt +
            $scope.eos.final.creditreceiptin +
            $scope.eos.final.storecredit +
            $scope.eos.final.storecreditin
          );
        };

        $scope.getOthersDifference = () => {
          if (!$scope.eos || !$scope.eos.final || !$scope.eos.original) {
            return 0;
          }

          return (
            $scope.eos.final.couponin -
            $scope.eos.original.couponin +
            $scope.eos.final.couponout -
            $scope.eos.original.couponout +
            $scope.eos.final.creditreceipt -
            $scope.eos.original.creditreceipt +
            $scope.eos.final.creditreceiptin -
            $scope.eos.original.creditreceiptin +
            $scope.eos.final.storecredit -
            $scope.eos.original.storecredit +
            $scope.eos.final.storecreditin -
            $scope.eos.original.storecreditin
          );
        };

        $scope.email = persist.get('eosEmail') ? persist.get('eosEmail') : '';

        const coinTypes = sessionJsonStorage.getItem('coinTypes');

        $scope.totals = {};
        $scope.eos = {};
        $scope.coinTotal = 0;

        $scope.eos = endOfShift.eos;

        $scope.balance = endOfShift.eos.expectedCashInDrawer;

        if (coinTypes) {
          $scope.coinTypes = coinTypes;
          $scope.calculateCoinsTotal();
        } else {
          $scope.coinTypes = _.map(cashUnits, (value) => ({
            value: value,
            qty: 0,
            total: 0,
          }));
        }

        $scope.setCoinsCount = (index) => {
          if (!angular.isNumber($scope.coinTypes[index].qty)) {
            $scope.coinTypes[index].qty = 0;
          }

          $scope.coinTypes[index].total =
            $scope.coinTypes[index].value * $scope.coinTypes[index].qty;

          $scope.calculateCoinsTotal();
        };

        delete $scope.eos.deposit;

        $scope.updateBalance();

        // condition to prevent someone from editing the same EOS twice
        if ($scope.eos.status === 'completed') {
          displayMessage.message({
            message: $translate.instant(
              'TheEOSHasAlreadyBeenProcessedForThisPeriod',
            ),
            ok: true,
            type: 'errorprompt',
          });
          return;
        }

        $scope.isCoupled = (key) => {
          if (!paymentTypes?.rules || !paymentTypes?.rules[key]) {
            return false;
          }

          return !!paymentTypes?.rules[key]?.coupled;
        };

        $scope.totals = endOfShift.totals;

        //Needs to save a transaction then print it just like withdrawel.
        $scope.openDrawer = () => {
          withdrawal.startNew().then((transaction) => {
            // init values on the transtaction
            pos.setCashier(transaction, pos.config.selectedCashier);
            pos.setDate(transaction);

            endOfShift.setShiftCycleId(transaction);

            transaction.withdrawelreason = 'EOS';

            withdrawal.add(transaction).then(() => {
              legacyPrint.withdrawal(transaction, pos.config);
            });
          });
        };

        $scope.sendToSubmitOverview = () => {
          endOfShift.coinTypes = $scope.coinTypes;
          endOfShift.coinTotal = $scope.coinTotal;
          endOfShift.cashDepositedInPOS = $scope.balance;
          endOfShift.eos.totalCashInDrawer = $scope.balance;

          navigateTo('pos.route#eos-submit-overview');
        };

        $scope.reset = () => {
          endOfShift.reset();

          $scope.totals = {};
          $scope.eos = {};
          $scope.coinTotal = 0;
          $scope.balance = 0;
        };

        $scope.cancel = () => {
          const callback = (result) => {
            if (result === 'yes') {
              $scope.reset();
              navigateTo('pos.route#more');
              $scope.$apply();
            }
          };
          const message = $translate.instant('AreYouSure');
          displayMessage.message({
            message: message,
            type: 'prompt',
            callback: callback,
          });
        };

        $scope.sumValues = function (list) {
          return sumBy(toArray(list), 'value');
        };

        $scope.openCoinCounterPopup = function () {
          $scope.coinCounterPopupOpened = true;
        };

        $scope.closeCoinCounterPopup = function () {
          $scope.coinCounterPopupOpened = false;
          sessionJsonStorage.setItem('coinTypes', $scope.coinTypes);
        };
      },
    }),
  );
